// src/router/guards.js
// Auth0 navigation guards for Vue Router

import { watchEffect } from 'vue';
import { useAuth0 } from '@auth0/auth0-vue';

/**
 * Global beforeEach guard.
 * 1. Waits for Auth0 to finish loading the session.
 * 2. Redirects unauthenticated users to login, saving the target route.
 * 3. Blocks /admin for non-admin users.
 * 4. Updates the document title from route.meta.title.
 */
export async function authGuardBeforeEach(to, from, next) {
  const { isLoading, isAuthenticated, user, loginWithRedirect } = useAuth0();

  // Wait for Auth0 to resolve the session
  if (isLoading.value) {
    await new Promise((resolve) => {
      const stop = watchEffect(() => {
        if (!isLoading.value) {
          stop();
          resolve();
        }
      });
    });
  }

  // If the route requires authentication and the user is not logged in
  if (to.matched.some(record => record.meta.requiresAuth) || to.path !== '/') {
    // Note: If you want all routes except landing/404 to require auth, 
    // check if they are authenticated:
    if (!isAuthenticated.value) {
      // Pass the current path into appState so Auth0 brings them back here post-login
      loginWithRedirect({
        appState: { targetUrl: to.fullPath }
      });
      return;
    }
  }

  // RBAC: Admin-only routes
  const roles = user.value?.['https://blything.co.uk/roles'] || [];
  const isAdmin = isAuthenticated.value && roles.includes('Admin');

  if (to.meta.requiresAdmin && !isAdmin) {
    return next('/');
  }

  // Update browser tab title
  document.title = to.meta.title || 'Blything';

  next();
}